import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import {
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { FocusScope } from "react-aria";
import { QRCodeSVG } from "qrcode.react";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import type {
  MemAgentChannelConnectionDialogState,
  MemAgentConnectionChannel,
} from "@/domains/metrics/tracked-event";
import {
  MDSButton,
  MDSButtonGroup,
  MDSButtonVariant,
  MDSDialog,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSLink,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { MDSZIndexes } from "@/mds/foundations";
import { clientEnvModule } from "@/modules/client-env";
import { copyTextToClipboard } from "@/modules/clipboard/copyTextToClipboard";
import { windowModule } from "@/modules/window";
import { useAppStore } from "@/store";
import type {
  IMessageConnectResponse,
  WhatsAppConnectResponse,
} from "@/store/mem-agent/mem-agent-store";

export type { MemAgentConnectionChannel } from "@/domains/metrics/tracked-event";

type ChannelActivation = WhatsAppConnectResponse | IMessageConnectResponse;
type DialogStep = MemAgentChannelConnectionDialogState;

interface SettingsMemAgentChannelConnectDialogProps {
  channel: MemAgentConnectionChannel | null;
  onClose: () => void;
}

const getChannelLabel = (channel: MemAgentConnectionChannel): string =>
  channel === "whatsapp" ? "WhatsApp" : "iMessage";

export const SettingsMemAgentChannelConnectDialog = observer(
  ({ channel, onClose }: SettingsMemAgentChannelConnectDialogProps) => {
    const { store } = useAppStore();
    const { memAgent } = store;
    const [step, setStep] = useState<DialogStep>("loading");
    const [activation, setActivation] = useState<ChannelActivation | null>(null);
    const attemptNumberRef = useRef(0);
    const connectionSessionRef = useRef(0);

    const handleClose = useCallback(() => {
      if (channel) {
        trackEvent(TrackedEvent.MemAgentChannelConnectDialogClosed, {
          channel,
          source: "settings",
          attempt_number: attemptNumberRef.current,
          dialog_state: step,
        });
      }

      onClose();
    }, [channel, onClose, step]);

    const requestActivation = useCallback(async () => {
      if (!channel) return;

      const connectionSession = connectionSessionRef.current;
      attemptNumberRef.current += 1;
      const attemptNumber = attemptNumberRef.current;

      setActivation(null);
      setStep("loading");
      trackEvent(TrackedEvent.MemAgentChannelConnectAttempt, {
        channel,
        source: "settings",
        attempt_number: attemptNumber,
      });

      const nextActivation =
        channel === "whatsapp"
          ? await memAgent.connectWhatsApp()
          : await memAgent.connectIMessage();

      if (connectionSessionRef.current !== connectionSession) return;

      if (!nextActivation) {
        setStep("failed");
        trackEvent(TrackedEvent.MemAgentChannelConnectFailed, {
          channel,
          source: "settings",
          attempt_number: attemptNumber,
          failure_reason: "activation_request_failed",
        });
        return;
      }

      setActivation(nextActivation);
      setStep("waiting");
      trackEvent(TrackedEvent.MemAgentChannelConnectActivationReady, {
        channel,
        source: "settings",
        attempt_number: attemptNumber,
      });
    }, [channel, memAgent]);

    useEffect(() => {
      if (!channel) return;

      let mounted = true;
      connectionSessionRef.current += 1;
      attemptNumberRef.current = 0;
      setActivation(null);
      setStep("loading");

      const initialize = async () => {
        await memAgent.reload();

        if (!mounted) return;

        const isConnected =
          channel === "whatsapp" ? memAgent.isWhatsAppConnected : memAgent.isIMessageConnected;

        if (isConnected) {
          setStep("success");
          return;
        }

        if (mounted) await requestActivation();
      };

      void initialize();
      return () => {
        mounted = false;
        connectionSessionRef.current += 1;
      };
    }, [channel, memAgent, requestActivation]);

    useEffect(() => {
      if (!channel || step !== "waiting" || !activation) return;

      let mounted = true;
      let pollTimeout: number | undefined;
      const expiresAt = Date.parse(activation.expires_at);
      const millisecondsUntilExpiration = expiresAt - Date.now();

      if (!Number.isFinite(expiresAt) || millisecondsUntilExpiration <= 0) {
        setActivation(null);
        setStep("expired");
        trackEvent(TrackedEvent.MemAgentChannelConnectFailed, {
          channel,
          source: "settings",
          attempt_number: attemptNumberRef.current,
          failure_reason: "activation_expired",
        });
        return;
      }

      const expirationTimeout = window.setTimeout(() => {
        setActivation(null);
        setStep("expired");
        trackEvent(TrackedEvent.MemAgentChannelConnectFailed, {
          channel,
          source: "settings",
          attempt_number: attemptNumberRef.current,
          failure_reason: "activation_expired",
        });
      }, millisecondsUntilExpiration);

      const pollConnection = async () => {
        await memAgent.reload();

        if (!mounted) return;

        const isConnected =
          channel === "whatsapp" ? memAgent.isWhatsAppConnected : memAgent.isIMessageConnected;

        if (isConnected) {
          setStep("success");
          trackEvent(TrackedEvent.MemAgentChannelConnectComplete, {
            channel,
            source: "settings",
            attempt_number: attemptNumberRef.current,
          });
          return;
        }

        pollTimeout = window.setTimeout(pollConnection, 2_000);
      };

      pollTimeout = window.setTimeout(pollConnection, 2_000);
      return () => {
        mounted = false;
        if (pollTimeout !== undefined) window.clearTimeout(pollTimeout);
        window.clearTimeout(expirationTimeout);
      };
    }, [activation, channel, memAgent, step]);

    useEffect(() => {
      if (!channel) return;

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key !== "Escape" || event.defaultPrevented) return;

        event.preventDefault();
        event.stopPropagation();
        handleClose();
      };

      window.addEventListener("keydown", handleKeyDown, true);
      return () => window.removeEventListener("keydown", handleKeyDown, true);
    }, [channel, handleClose]);

    const openOnThisDevice = useCallback(async () => {
      if (!activation || !channel) return;

      trackEvent(TrackedEvent.MemAgentChannelConnectOpen, {
        channel,
        source: "settings",
        attempt_number: attemptNumberRef.current,
      });

      if ("activation_message" in activation) {
        await copyTextToClipboard({ text: activation.activation_message });
      }

      await windowModule.navigateToExternalAndPreserveHistory({
        url: new URL(activation.activation_url),
      });
    }, [activation, channel]);

    const handleOverlayClick = useCallback(
      (event: ReactMouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget) handleClose();
      },
      [handleClose]
    );

    if (!channel) return null;

    const channelLabel = getChannelLabel(channel);
    const showSameDeviceLink = channel !== "imessage" || !clientEnvModule.isWindows();

    return (
      <StyledOverlay data-mds-name="mem-agent-channel-connect-overlay" onClick={handleOverlayClick}>
        <FocusScope contain restoreFocus autoFocus>
          <StyledDialogContainer tabIndex={-1} onClick={event => event.stopPropagation()}>
            <StyledDialogShell>
              <MDSDialog isOpen handleClose={handleClose}>
                <DialogTitle slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
                  Connect {channelLabel}
                </DialogTitle>
                <DialogContent slot={MDSDialogSlotName.Content}>
                  {step === "loading" ? <StatusCopy>Preparing your QR code…</StatusCopy> : null}

                  {step === "waiting" && activation ? (
                    <StatusStack>
                      <Instructions>
                        <MDSText variant={MDSTextVariant.TextM} weight={MDSTextWeight.Smedium}>
                          Scan with your phone
                        </MDSText>
                        <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Secondary}>
                          Open your phone’s camera and scan this code. Then send the prepared
                          message to connect Mem Agent.
                        </MDSText>
                      </Instructions>
                      <QRCodeFrame>
                        <QRCodeSVG
                          value={activation.activation_url}
                          size={224}
                          level="M"
                          marginSize={4}
                          title={`QR code to connect Mem Agent on ${channelLabel}`}
                        />
                      </QRCodeFrame>
                      {showSameDeviceLink ? (
                        <SameDeviceLink variant={MDSTextVariant.TextS} onPress={openOnThisDevice}>
                          {channel === "whatsapp"
                            ? "Use WhatsApp on this device"
                            : "Use Messages on this device"}
                        </SameDeviceLink>
                      ) : null}
                    </StatusStack>
                  ) : null}

                  {step === "success" ? (
                    <StatusStack>
                      <MDSText variant={MDSTextVariant.TextM} weight={MDSTextWeight.Smedium}>
                        Connected to Mem Agent
                      </MDSText>
                      <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Secondary}>
                        You can keep chatting with your Agent in {channelLabel} anytime.
                      </MDSText>
                      <MDSButton variant={MDSButtonVariant.Contrast} onPress={handleClose}>
                        Done
                      </MDSButton>
                    </StatusStack>
                  ) : null}

                  {step === "failed" ? (
                    <StatusStack>
                      <ErrorCopy>
                        We couldn’t start the {channelLabel} connection. Please try again.
                      </ErrorCopy>
                      <MDSButtonGroup>
                        <MDSButton variant={MDSButtonVariant.Contrast} onPress={requestActivation}>
                          Try again
                        </MDSButton>
                      </MDSButtonGroup>
                    </StatusStack>
                  ) : null}

                  {step === "expired" ? (
                    <StatusStack>
                      <ErrorCopy>This connection message expired. Please try again.</ErrorCopy>
                      <MDSButtonGroup>
                        <MDSButton variant={MDSButtonVariant.Contrast} onPress={requestActivation}>
                          Try again
                        </MDSButton>
                      </MDSButtonGroup>
                    </StatusStack>
                  ) : null}
                </DialogContent>
              </MDSDialog>
            </StyledDialogShell>
          </StyledDialogContainer>
        </FocusScope>
      </StyledOverlay>
    );
  }
);

const StyledOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${MDSZIndexes.StandardModal};
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(0 0 0 / 14%);
`;

const StyledDialogContainer = styled.div`
  outline: none;
`;

const StyledDialogShell = styled.div`
  .mds-dialog {
    width: min(579px, calc(100vw - 32px));
  }

  [data-mds-name="dialog-body"] {
    scrollbar-gutter: auto;
  }
`;

const DialogTitle = styled(MDSText)`
  box-sizing: border-box;
  flex: 1;
  padding-left: 46px;
  text-align: center;
`;

const DialogContent = styled(MDSDialogTextContent)`
  align-items: center;
  text-align: center;
`;

const StatusStack = styled.div`
  display: flex;
  width: min(360px, calc(100vw - 80px));
  align-items: center;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  text-align: center;
`;

const Instructions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  text-align: center;
`;

const QRCodeFrame = styled.div`
  align-self: center;
  width: min(224px, 100%);
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: #fff;

  svg {
    display: block;
    width: 100%;
    height: auto;
  }
`;

const SameDeviceLink = styled(MDSLink)`
  align-self: center;
`;

const StatusCopy = styled(MDSText)`
  width: min(360px, calc(100vw - 80px));
`;

const ErrorCopy = styled(MDSText)`
  color: ${({ theme }) => theme.colors.text.danger};
`;
